// SPDX-License-Identifier: MIT
// Editor features selector: access drawn polygons from Kepler.gl editor state

import { RootState } from '../store';
import type { KeplerEditorFeature } from '../types/kepler-features';

/**
 * Select all drawn features from the Kepler.gl editor
 * Editor features are stored as GeoJSON-like objects in visState.editor.
 *
 * NOTE: Only returns committed features from `editor.features`. We deliberately
 * do NOT include `editor.selectedFeature` here because:
 *  1. While a feature is actively being drawn it lives in `selectedFeature` but
 *     is not yet a completed polygon — it cannot be annotated.
 *  2. After a feature is deleted Kepler may keep a stale `selectedFeature`
 *     reference; including it would create "ghost" entries in the annotations
 *     panel that survive deletion and prevent the automatic cleanup effect from
 *     closing the edit view, ultimately causing a React "Maximum update depth
 *     exceeded" crash (React error #185).
 */
export const selectEditorFeatures = (state: RootState): KeplerEditorFeature[] => {
  const keplerState = (state as any).keplerGl?.map;
  if (!keplerState) return [];

  const editor = keplerState.visState?.editor;
  if (!editor) return [];

  return editor.features || [];
};

/**
 * Check if there are any drawn features
 */
export const selectHasEditorFeatures = (state: RootState): boolean => {
  const features = selectEditorFeatures(state);
  return features.length > 0;
};

/**
 * Count of drawn polygon features
 */
export const selectEditorFeatureCount = (state: RootState): number => {
  return selectEditorFeatures(state).length;
};
